const HOTSPOT_MODEL_VIEWER_TAG_NAME = "fishymap-hotspot-model-viewer";
const MODEL_VIEWER_MODULE_URL = "/js/model-viewer.min.js";
const HTMLElementBase = globalThis.HTMLElement ?? class {};

let modelViewerModulePromise = null;

function trimString(value) {
  const normalized = String(value ?? "").trim();
  return normalized || "";
}

function escapeHtml(value) {
  return String(value ?? "").replace(
    /[&<>\"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[char] || char,
  );
}

function spriteIcon(name, sizeClass = "size-4") {
  return `<svg class="fishy-icon ${sizeClass}" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-${name}"></use></svg>`;
}

const BUTTON_SHADOWLESS_CLASS = "shadow-none hover:shadow-none focus:shadow-none active:shadow-none";
const BUTTON_ACTIVE_CLASS = "btn-active";

function parseAssets(value) {
  try {
    const parsed = JSON.parse(trimString(value) || "[]");
    return (Array.isArray(parsed) ? parsed : [])
      .map((asset, index) => ({
        key: trimString(asset?.key) || `model-${index}`,
        kind: trimString(asset?.kind),
        label: trimString(asset?.label) || `Model ${index + 1}`,
        src: trimString(asset?.src),
        animated: asset?.animated !== false,
        shadowIntensity: trimString(asset?.shadowIntensity),
        environmentImage: trimString(asset?.environmentImage),
        exposure: trimString(asset?.exposure),
        cameraOrbit: trimString(asset?.cameraOrbit),
        cameraTarget: trimString(asset?.cameraTarget),
        fieldOfView: trimString(asset?.fieldOfView),
      }))
      .filter((asset) => asset.src);
  } catch {
    return [];
  }
}

function isAbsoluteUrl(value) {
  return /^[a-z][a-z0-9+.-]*:/i.test(value) || value.startsWith("//");
}

function resolveModelUrl(src) {
  const normalized = trimString(src);
  if (!normalized || isAbsoluteUrl(normalized)) {
    return normalized;
  }
  const resolver = globalThis.window?.__fishystuffResolveCdnUrl;
  if (typeof resolver === "function") {
    return trimString(resolver(normalized)) || normalized;
  }
  return normalized;
}

function loadModelViewerModule() {
  if (globalThis.customElements?.get?.("model-viewer")) {
    return Promise.resolve();
  }
  if (!modelViewerModulePromise) {
    const loader =
      globalThis.window?.__fishystuffImportModelViewer ||
      ((url) => import(url));
    modelViewerModulePromise = Promise.resolve(loader(MODEL_VIEWER_MODULE_URL)).catch((error) => {
      modelViewerModulePromise = null;
      throw error;
    });
  }
  return modelViewerModulePromise;
}

function buttonTitle(command, active) {
  switch (command) {
    case "reset-camera":
      return "Reset camera";
    case "toggle-rotate":
      return active ? "Pause rotation" : "Play rotation";
    case "toggle-animation":
      return active ? "Pause animation" : "Play animation";
    default:
      return "";
  }
}

function commandIcon(command, active) {
  switch (command) {
    case "reset-camera":
      return "camera-rotate";
    case "toggle-rotate":
      return active ? "pause-fill" : "rotate-y-fill";
    case "toggle-animation":
      return active ? "pause-fill" : "play-fill";
    default:
      return "cube-view";
  }
}

export class FishyMapHotspotModelViewerElement extends HTMLElementBase {
  static get observedAttributes() {
    return [
      "data-assets",
      "data-active-key",
      "data-action-label",
      "data-action-value",
      "data-action-icon",
    ];
  }

  constructor() {
    super();
    this._rotate = true;
    this._animate = true;
    this._hasAnimations = false;
    this._loadState = "idle";
    this._handleClick = (event) => {
      const commandButton = event.target.closest?.("button[data-hotspot-model-command]");
      if (!commandButton) {
        return;
      }
      event.preventDefault?.();
      event.stopPropagation?.();
      this.runCommand(trimString(commandButton.getAttribute("data-hotspot-model-command")));
    };
    this._handleViewerLoad = (event) => {
      if (event.target !== this.viewerElement()) {
        return;
      }
      this._loadState = "loaded";
      this.updateAnimationAvailability();
      this.syncViewerPlayback();
      this.updateControls();
    };
    this._handleViewerError = (event) => {
      if (event.target !== this.viewerElement()) {
        return;
      }
      this._loadState = "failed";
      this.updateControls();
    };
  }

  attributeChangedCallback(_name, previousValue, nextValue) {
    if (previousValue === nextValue) {
      return;
    }
    this._rotate = true;
    this._animate = true;
    this._hasAnimations = false;
    this._loadState = "idle";
    this.render();
    void this.loadViewer();
  }

  connectedCallback() {
    this.addEventListener("click", this._handleClick);
    this.addEventListener("load", this._handleViewerLoad, true);
    this.addEventListener("error", this._handleViewerError, true);
    this.render();
    void this.loadViewer();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this._handleClick);
    this.removeEventListener("load", this._handleViewerLoad, true);
    this.removeEventListener("error", this._handleViewerError, true);
  }

  assets() {
    return parseAssets(this.getAttribute?.("data-assets"));
  }

  activeAsset() {
    const assets = this.assets();
    if (!assets.length) {
      return null;
    }
    const activeKey = trimString(this.getAttribute?.("data-active-key"));
    return assets.find((asset) => asset.key === activeKey) || assets[0];
  }

  hotspotAction() {
    const value = trimString(this.getAttribute?.("data-action-value"));
    if (!value) {
      return null;
    }
    return {
      label: trimString(this.getAttribute?.("data-action-label")) || "Hotspot Action",
      value,
      icon: trimString(this.getAttribute?.("data-action-icon")) || "cube-view",
    };
  }

  viewerElement() {
    return this.querySelector?.("model-viewer") || null;
  }

  async loadViewer() {
    if (!this.isConnected || !this.activeAsset()) {
      return;
    }
    try {
      await loadModelViewerModule();
      this._loadState = this._loadState === "idle" ? "loading" : this._loadState;
      this.syncViewerPlayback();
      this.updateControls();
    } catch {
      this._loadState = "failed";
      this.updateControls();
    }
  }

  updateAnimationAvailability() {
    if (this.activeAsset()?.animated === false) {
      this._hasAnimations = false;
      return;
    }
    const viewer = this.viewerElement();
    const animations = Array.isArray(viewer?.availableAnimations)
      ? viewer.availableAnimations
      : [];
    this._hasAnimations = animations.length > 0;
  }

  syncViewerPlayback() {
    const viewer = this.viewerElement();
    if (!viewer) {
      return;
    }
    if (this._rotate) {
      viewer.setAttribute("auto-rotate", "");
    } else {
      viewer.removeAttribute("auto-rotate");
    }
    if (this._animate && this._hasAnimations && this.activeAsset()?.animated !== false) {
      viewer.setAttribute("autoplay", "");
      viewer.play?.();
    } else {
      viewer.removeAttribute("autoplay");
      viewer.pause?.();
    }
  }

  runCommand(command) {
    const viewer = this.viewerElement();
    switch (command) {
      case "reset-camera":
        if (viewer) {
          const activeAsset = this.activeAsset();
          viewer.cameraOrbit = trimString(activeAsset?.cameraOrbit) || "0deg 75deg auto";
          viewer.fieldOfView = trimString(activeAsset?.fieldOfView) || "30deg";
          viewer.resetTurntableRotation?.();
          viewer.jumpCameraToGoal?.();
        }
        break;
      case "toggle-rotate":
        this._rotate = !this._rotate;
        this.syncViewerPlayback();
        this.updateControls();
        break;
      case "toggle-animation":
        if (!this._hasAnimations) {
          return;
        }
        this._animate = !this._animate;
        this.syncViewerPlayback();
        this.updateControls();
        break;
      default:
        break;
    }
  }

  updateControls() {
    const rotateButton = this.querySelector?.("[data-hotspot-model-command='toggle-rotate']");
    if (rotateButton) {
      const title = buttonTitle("toggle-rotate", this._rotate);
      rotateButton.className = this.commandButtonClass(this._rotate, false);
      rotateButton.setAttribute("aria-label", title);
      rotateButton.setAttribute("data-fishy-tooltip", title);
      rotateButton.setAttribute("aria-pressed", this._rotate ? "true" : "false");
      rotateButton.innerHTML = spriteIcon(commandIcon("toggle-rotate", this._rotate), "size-4");
    }

    const animationButton = this.querySelector?.("[data-hotspot-model-command='toggle-animation']");
    if (animationButton) {
      const title = buttonTitle("toggle-animation", this._animate);
      animationButton.removeAttribute?.("disabled");
      animationButton.className = this.commandButtonClass(this._animate, !this._hasAnimations);
      animationButton.setAttribute(
        "aria-label",
        this._hasAnimations ? title : "No animation",
      );
      animationButton.setAttribute(
        "data-fishy-tooltip",
        this._hasAnimations ? title : "No animation",
      );
      animationButton.setAttribute("aria-disabled", this._hasAnimations ? "false" : "true");
      animationButton.setAttribute("aria-pressed", this._animate ? "true" : "false");
      animationButton.innerHTML = spriteIcon(
        commandIcon("toggle-animation", this._animate),
        "size-4",
      );
    }

    const status = this.querySelector?.("[data-hotspot-model-status]");
    if (status) {
      status.hidden = this._loadState !== "failed";
    }
  }

  assetButtonMarkup(asset, index) {
    const active = asset.key === this.activeAsset()?.key;
    const icon = this.assetIcon(asset);
    return `
      <button
        class="btn btn-sm join-item min-w-0 flex-1 gap-1.5 px-2 ${BUTTON_SHADOWLESS_CLASS} ${active ? BUTTON_ACTIVE_CLASS : "btn-ghost"}"
        type="button"
        data-hotspot-model-asset-index="${escapeHtml(index)}"
        data-on:click="$_map_ui.windowUi.zoneInfo.hotspotModelTab = '${escapeHtml(asset.key)}'"
        aria-pressed="${active ? "true" : "false"}"
      >
        ${spriteIcon(icon, "size-3.5")}
        <span class="truncate">${escapeHtml(asset.label)}</span>
      </button>
    `;
  }

  assetIcon(asset) {
    switch (trimString(asset?.kind)) {
      case "fish":
        return "fish-fill";
      case "spot":
      case "hotspot":
        return "wave-fill";
      case "scene":
      case "seagulls":
        return "bird-fill";
      default:
        return "cube-view";
    }
  }

  commandButtonClass(active, disabled) {
    const tone = disabled
      ? "btn-disabled"
      : active
        ? BUTTON_ACTIVE_CLASS
        : "btn-ghost";
    return `btn btn-square btn-sm join-item ${BUTTON_SHADOWLESS_CLASS} ${tone}`;
  }

  commandButtonMarkup(command, active, disabled = false) {
    const title = buttonTitle(command, active);
    return `
      <button
        class="${this.commandButtonClass(active, disabled)}"
        type="button"
        data-hotspot-model-command="${escapeHtml(command)}"
        aria-label="${escapeHtml(disabled ? "No animation" : title)}"
        data-fishy-tooltip="${escapeHtml(disabled ? "No animation" : title)}"
        aria-pressed="${active ? "true" : "false"}"
        aria-disabled="${disabled ? "true" : "false"}"
      >${spriteIcon(commandIcon(command, active), "size-4")}</button>
    `;
  }

  render() {
    const assets = this.assets();
    if (!assets.length) {
      this.innerHTML = "";
      return;
    }
    const activeAsset = this.activeAsset();
    const resolvedSrc = resolveModelUrl(activeAsset.src);
    const allowAnimation = activeAsset.animated !== false;
    const environmentImage = trimString(activeAsset.environmentImage);
    const shadowIntensity = trimString(activeAsset.shadowIntensity) || "0.35";
    const exposure = trimString(activeAsset.exposure) || "1";
    const cameraOrbit = trimString(activeAsset.cameraOrbit) || "0deg 75deg auto";
    const cameraTarget = trimString(activeAsset.cameraTarget);
    const fieldOfView = trimString(activeAsset.fieldOfView) || "30deg";
    const hotspotAction = this.hotspotAction();
    this.innerHTML = `
      <div class="rounded-box overflow-hidden border border-base-300/80 bg-base-100/80 shadow-xs">
        <div class="flex flex-wrap items-center gap-2 border-b border-base-300/70 bg-base-200/70 p-2.5">
          <div class="join join-horizontal min-w-0 flex-1">
            ${assets.map((asset, index) => this.assetButtonMarkup(asset, index)).join("")}
          </div>
          <div class="join join-horizontal shrink-0">
            ${this.commandButtonMarkup("reset-camera", false)}
            ${this.commandButtonMarkup("toggle-rotate", this._rotate)}
            ${this.commandButtonMarkup("toggle-animation", this._animate, !this._hasAnimations)}
          </div>
        </div>
        <div class="relative aspect-[4/3] min-h-48 bg-base-300/35">
          <model-viewer
            class="block size-full"
            src="${escapeHtml(resolvedSrc)}"
            camera-controls
            touch-action="pan-y"
            interaction-prompt="none"
            loading="eager"
            reveal="auto"
            shadow-intensity="${escapeHtml(shadowIntensity)}"
            exposure="${escapeHtml(exposure)}"
            ${environmentImage ? `environment-image="${escapeHtml(environmentImage)}"` : ""}
            rotation-per-second="18deg"
            camera-orbit="${escapeHtml(cameraOrbit)}"
            ${cameraTarget ? `camera-target="${escapeHtml(cameraTarget)}"` : ""}
            field-of-view="${escapeHtml(fieldOfView)}"
            ${this._rotate ? "auto-rotate" : ""}
            ${this._animate && allowAnimation ? "autoplay" : ""}
          ></model-viewer>
          <div
            class="absolute inset-x-2 bottom-2 rounded bg-error/90 px-2 py-1 text-[11px] font-medium text-error-content"
            data-hotspot-model-status
            hidden
          >Viewer unavailable</div>
        </div>
        ${
          hotspotAction
            ? `<div class="flex items-center justify-between gap-2 border-t border-base-300/70 bg-base-100 px-2.5 py-2 text-xs">
                <span class="inline-flex min-w-0 items-center gap-1.5 font-medium text-base-content/65">
                  ${spriteIcon(hotspotAction.icon, "size-4")}
                  <span class="truncate">${escapeHtml(hotspotAction.label)}</span>
                </span>
                <span class="badge badge-soft badge-sm shrink-0">${escapeHtml(hotspotAction.value)}</span>
              </div>`
            : ""
        }
      </div>
    `;
    this.syncViewerPlayback();
    this.updateControls();
  }
}

export function registerFishyMapHotspotModelViewerElement(registry = globalThis.customElements) {
  if (!registry || registry.get(HOTSPOT_MODEL_VIEWER_TAG_NAME)) {
    return true;
  }
  registry.define(HOTSPOT_MODEL_VIEWER_TAG_NAME, FishyMapHotspotModelViewerElement);
  return true;
}

registerFishyMapHotspotModelViewerElement();
